import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Play, Download, BarChart3, FileText, Settings, AlertCircle } from 'lucide-react';
import GeneralAnalyticsComponent from './GeneralAnalyticsComponent';
import AnalyticsSetup from './AnalyticsSetup';
import OpinionQuotas from './OpinionQuotas';

// Error boundary for analytics components
class AnalyticsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    console.error('Analytics component error:', error);
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex gap-4 items-start p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <AlertCircle className="text-yellow-600 flex-shrink-0" size={20} />
            <div>
              <h3 className="font-semibold text-yellow-900">Component Error</h3>
              <p className="text-sm text-yellow-800 mt-1">There was an error loading this component. Check the console for details.</p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Survey tabs — "All Logs" shows everything the account can see; the others scope to one script.
const SURVEYS = [
  { key: 'all', name: 'All Logs', scriptId: null },
  { key: 'survey', name: 'Survey', scriptId: '6a90a34143673ea8265d17ce' },
  { key: 'nyc', name: 'NYC Survey', scriptId: '6a95cdc40349ba1bbb1238d6' },
];

const OpinionLogs = () => {
  const navigate = useNavigate();
  const [allLogs, setAllLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [playingRecordingId, setPlayingRecordingId] = useState(null);
  const [recordingErrors, setRecordingErrors] = useState({});
  const [activeTab, setActiveTab] = useState('logs'); // logs | analysis | setup | quotas
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedSurveyKey, setSelectedSurveyKey] = useState('all');
  const [sortBy, setSortBy] = useState('recent'); // recent | oldest | longest | shortest
  const activeSurvey = useMemo(
    () => SURVEYS.find((s) => s.key === selectedSurveyKey) || SURVEYS[0],
    [selectedSurveyKey]
  );
  const activeScriptId = activeSurvey.scriptId; // null on the "All Logs" tab
  // Analytics/Setup/Quotas stay pinned to a real script (first survey that has a scriptId).
  const ANALYTICS_SCRIPT_ID = SURVEYS.find((s) => s.scriptId)?.scriptId || null;

  // Client-side filter: "All Logs" (no scriptId) shows everything; otherwise scope to the survey.
  const logs = useMemo(
    () =>
      allLogs.filter((log) => {
        if (!activeScriptId) return true; // All Logs
        const logScriptId = log.scriptId?.$oid || log.scriptId || '';
        return logScriptId === activeScriptId;
      }),
    [allLogs, activeScriptId]
  );

  // Sorted view for rendering (stats stay on `logs`, which is order-independent).
  const sortedLogs = useMemo(() => {
    const dur = (l) => Number(l.duration) || 0;
    const time = (l) => new Date(l.callStartTime || l.createdAt || 0).getTime();
    const arr = [...logs];
    switch (sortBy) {
      case 'longest': arr.sort((a, b) => dur(b) - dur(a)); break;
      case 'shortest': arr.sort((a, b) => dur(a) - dur(b)); break;
      case 'oldest': arr.sort((a, b) => time(a) - time(b)); break;
      case 'recent':
      default: arr.sort((a, b) => time(b) - time(a)); break;
    }
    return arr;
  }, [logs, sortBy]);

  // Check authentication and sync token
  useEffect(() => {
    const authenticated = sessionStorage.getItem('opinionServiceAuthenticated');
    const token = localStorage.getItem('token');

    if (!authenticated || !token) {
      navigate('/opinion-service-login');
    } else {
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch logs after authentication
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchLogs = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const apiBase = process.env.REACT_APP_AGENT_API_URL || '';

        if (!token) {
          console.error('No token available for logs fetch');
          setLoading(false);
          return;
        }

        const response = await fetch(`${apiBase}/api/agentlogs`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`API returned ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();
        if (data.success && data.logs) {
          // Store all logs; per-survey filtering happens client-side via the `logs` memo.
          setAllLogs(data.logs);
        }
      } catch (err) {
        console.error('Error fetching logs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [isAuthenticated]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const parseTranscript = (transcript) => {
    if (!transcript) return [];
    if (typeof transcript === 'string') {
      const lines = transcript.split('\n').filter(l => l.trim());
      return lines.map(line => {
        if (line.startsWith('Agent:')) {
          return { role: 'agent', text: line.replace(/^Agent:\s*['"]?/, '').replace(/['"]?\s*$/, '') };
        } else if (line.startsWith('User:')) {
          return { role: 'user', text: line.replace(/^User:\s*['"]?/, '').replace(/['"]?\s*$/, '') };
        }
        return null;
      }).filter(Boolean);
    }
    return Array.isArray(transcript) ? transcript : [];
  };

  const getMoodEmoji = (mood) => {
    const moodMap = { positive: '😊', neutral: '😐', negative: '😞', unknown: '❓' };
    return moodMap[mood] || '❓';
  };

  const handlePlayRecording = async (log) => {
    const callSid = log.callSid || log._id?.$oid || log._id;
    setPlayingRecordingId(callSid);
    setRecordingErrors(prev => ({ ...prev, [callSid]: null }));

    try {
      // Try sessionStorage first (OpinionService), then localStorage
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('[Recording] Using token, auth header:', `Bearer ${token.substring(0, 20)}...`);
      } else {
        console.log('[Recording] No token found');
      }

      // Fetch recording as blob
      const response = await fetch(`${apiBase}/api/call-recording/${callSid}`, { headers });

      if (!response.ok) {
        throw new Error('Failed to load recording');
      }

      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      const audio = new Audio();
      audio.src = audioUrl;
      audio.controls = true;
      audio.style.width = '100%';
      audio.style.marginTop = '10px';

      // Remove existing audio if any
      const existingAudio = document.getElementById(`recording-${callSid}`);
      if (existingAudio) {
        existingAudio.remove();
      }

      audio.id = `recording-${callSid}`;
      const container = document.getElementById(`recording-container-${callSid}`);
      if (container) {
        container.appendChild(audio);
        audio.play();
      }
    } catch (err) {
      console.error('Error playing recording:', err);
      setRecordingErrors(prev => ({ ...prev, [callSid]: err.message }));
    } finally {
      setPlayingRecordingId(null);
    }
  };

  const handleDownloadRecording = async (log) => {
    const callSid = log.callSid || log._id?.$oid || log._id;

    try {
      // Try sessionStorage first (OpinionService), then localStorage
      const token = sessionStorage.getItem('token') || localStorage.getItem('token');
      const apiBase = process.env.REACT_APP_AGENT_API_URL || '';
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${apiBase}/api/call-recording/${callSid}`, { headers });

      if (!response.ok) {
        throw new Error('Failed to download recording');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `call-${callSid}.mp3`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading recording:', err);
      alert(`Failed to download: ${err.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/opinion-service')}
              className="p-2 hover:bg-gray-100 rounded transition"
              title="Back to dashboard"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">📊 Opinion Services Logs</h1>
              <p className="text-gray-600 text-sm mt-1">Call transcripts and responses</p>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('logs')}
              className={`py-4 px-1 font-medium text-sm border-b-2 transition ${
                activeTab === 'logs'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText size={18} />
                Logs
              </div>
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 px-1 font-medium text-sm border-b-2 transition ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 size={18} />
                Analytics
              </div>
            </button>
            <button
              onClick={() => setActiveTab('setup')}
              className={`py-4 px-1 font-medium text-sm border-b-2 transition ${
                activeTab === 'setup'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <Settings size={18} />
                Analytics Setup
              </div>
            </button>
            <button
              onClick={() => setActiveTab('quotas')}
              className={`py-4 px-1 font-medium text-sm border-b-2 transition ${
                activeTab === 'quotas'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 size={18} />
                Quotas
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'analytics' && isAuthenticated ? (
          <AnalyticsErrorBoundary>
            <GeneralAnalyticsComponent allowedScriptId={ANALYTICS_SCRIPT_ID} />
          </AnalyticsErrorBoundary>
        ) : activeTab === 'setup' && isAuthenticated ? (
          <AnalyticsErrorBoundary>
            <AnalyticsSetup allowedScriptId={ANALYTICS_SCRIPT_ID} />
          </AnalyticsErrorBoundary>
        ) : activeTab === 'quotas' && isAuthenticated ? (
          <OpinionQuotas scriptId={ANALYTICS_SCRIPT_ID} />
        ) : activeTab === 'logs' ? (
          <>
            {/* Survey sub-tabs — scope the logs list below to one script */}
            <div className="flex gap-6 border-b border-gray-200 mb-6">
              {SURVEYS.map((survey) => (
                <button
                  key={survey.key}
                  onClick={() => {
                    setSelectedSurveyKey(survey.key);
                    setExpandedLogId(null);
                  }}
                  className={`pb-3 px-1 font-semibold text-sm border-b-2 transition ${
                    selectedSurveyKey === survey.key
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {survey.name}
                </button>
              ))}
            </div>

            {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-600">Loading logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <div className="text-6xl mb-4">📈</div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">No Logs Found</h3>
              <p className="text-gray-600">No calls or responses yet for this script.</p>
            </div>
          ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-gray-600 text-sm font-medium">Total Calls</h3>
                <p className="text-3xl font-bold text-gray-900 mt-2">{logs.length}</p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-gray-600 text-sm font-medium">Average Duration</h3>
                <p className="text-3xl font-bold text-gray-900 mt-2">
                  {logs.length > 0
                    ? formatDuration(Math.round(logs.reduce((sum, log) => sum + (log.duration || 0), 0) / logs.length))
                    : '-'
                  }
                </p>
              </div>
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-gray-600 text-sm font-medium">Status</h3>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {logs.filter(l => l.hangupReason === 'completed').length}/{logs.length} Completed
                </p>
              </div>
            </div>

            {/* Logs List */}
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Call Logs</h2>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                Sort by
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                >
                  <option value="recent">Most recent</option>
                  <option value="oldest">Oldest</option>
                  <option value="longest">Longest call</option>
                  <option value="shortest">Shortest call</option>
                </select>
              </label>
            </div>
            <div className="space-y-3">
              {sortedLogs.map((log) => {
                const logId = log._id?.$oid || log._id;
                const isExpanded = expandedLogId === logId;
                const transcript = parseTranscript(log.transcript);
                return (
                  <div key={logId} className="bg-white rounded-lg shadow hover:shadow-md transition">
                    {/* Log Header - Clickable */}
                    <button
                      onClick={() => setExpandedLogId(isExpanded ? null : logId)}
                      className="w-full px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition"
                    >
                      <div className="text-left flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-gray-900">{log.phoneNumber}</h3>
                          <span className="text-xl">{getMoodEmoji(log.mood)}</span>
                          {log.outcome === 'success' && <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Completed</span>}
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{formatDate(log.callStartTime || log.createdAt)}</p>
                        {log.summary && (
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{log.summary}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-4 ml-4 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">{formatDuration(log.duration)}</p>
                          <p className="text-xs text-gray-500">{transcript.length} exchanges</p>
                        </div>
                        {isExpanded ? (
                          <ChevronUp size={20} className="text-gray-400" />
                        ) : (
                          <ChevronDown size={20} className="text-gray-400" />
                        )}
                      </div>
                    </button>

                    {/* Log Details - Expandable */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 space-y-4">
                        {/* Summary */}
                        {log.summary && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2">Summary</h4>
                            <p className="text-sm text-gray-700 leading-relaxed">{log.summary}</p>
                          </div>
                        )}

                        {/* Recording */}
                        {log.callSid && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">🎙️ Call Recording</h4>
                            <div className="bg-white rounded border border-gray-200 p-4">
                              <div className="flex gap-2 mb-3">
                                <button
                                  onClick={() => handlePlayRecording(log)}
                                  disabled={playingRecordingId === (log.callSid || log._id?.$oid || log._id)}
                                  className="inline-flex items-center gap-1 rounded-lg border border-blue-500 px-3 py-1.5 text-sm text-blue-700 hover:bg-blue-50 disabled:opacity-50 transition"
                                >
                                  <Play className="w-4 h-4" />
                                  {playingRecordingId === (log.callSid || log._id?.$oid || log._id) ? 'Loading...' : 'Play'}
                                </button>
                                <button
                                  onClick={() => handleDownloadRecording(log)}
                                  className="inline-flex items-center gap-1 rounded-lg border border-green-500 px-3 py-1.5 text-sm text-green-700 hover:bg-green-50 transition"
                                >
                                  <Download className="w-4 h-4" />
                                  Download
                                </button>
                              </div>
                              {recordingErrors[log.callSid || log._id?.$oid || log._id] && (
                                <p className="text-sm text-red-600 mb-2">
                                  Error: {recordingErrors[log.callSid || log._id?.$oid || log._id]}
                                </p>
                              )}
                              <div id={`recording-container-${log.callSid || log._id?.$oid || log._id}`} className="mt-2"></div>
                            </div>
                          </div>
                        )}

                        {/* Transcript */}
                        {transcript.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">Call Transcript</h4>
                            <div className="bg-white rounded border border-gray-200 p-4 max-h-96 overflow-y-auto space-y-2 text-sm">
                              {transcript.map((msg, idx) => (
                                <div key={idx} className={`p-3 rounded ${msg.role === 'agent' ? 'bg-blue-50 border-l-4 border-blue-400' : 'bg-gray-50'}`}>
                                  <p className={`font-medium text-xs uppercase ${msg.role === 'agent' ? 'text-blue-700' : 'text-gray-600'}`}>
                                    {msg.role === 'agent' ? 'Agent' : 'Caller'}
                                  </p>
                                  <p className="text-gray-800 mt-1">{msg.text}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
          )}
          </>
        ) : null}
      </main>
    </div>
  );
};

export default OpinionLogs;
